import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { AcoesDaLinha, BotaoVer, BotaoEditar, BotaoExcluir } from '../../components/BotoesAcao';
import { useConfirm } from '../../components/ConfirmDialog';
import { Veiculo } from '../../types';
import { veiculosService, VeiculosResponse } from '../../services/veiculos';
import ExportButtons from '../../components/ExportButtons';
import { exportService } from '../../services/export';
import { parseApiDate } from '../../utils/formatters';
import { usePermissoes } from '../../hooks/usePermissoes';
import AvisoSemPermissao from '../../components/AvisoSemPermissao';
import { ehSemPermissao, mensagemDoErro } from '../../utils/erros';
import { catalogoVeiculosService } from '../../services/catalogoVeiculos';
import { VeiculoMarca, VeiculoModelo } from '../../types';

// Vencido = a data já passou, comparando DIA, não instante. Usar new Date() direto
// acendia o alerta às 21:00 da véspera (a data chega como meia-noite UTC).
type SituacaoDoDocumento = 'vencido' | 'vencendo' | 'em_dia' | 'sem_data';

/**
 * Situacao de um documento do veiculo na listagem.
 *
 * Tres estados, e nao apenas vencido ou nao: um licenciamento que vence na
 * semana que vem exige agenda hoje, e antes aparecia igual a um que vence em
 * dois anos. A janela de 30 dias e a mesma que o alerta do sistema usa.
 *
 * O veiculo vendido saiu dos alertas de documento — nao e mais patrimonio da
 * empresa. Esta listagem passou a ser o lugar onde essa situacao continua
 * visivel, entao ela precisa dizer mais do que "vermelho ou verde".
 */
const situacaoDoDocumento = (data?: string | null): SituacaoDoDocumento => {
  const venc = data ? parseApiDate(data) : null;
  if (!venc) return 'sem_data';

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  if (venc < hoje) return 'vencido';

  const em30Dias = new Date(hoje);
  em30Dias.setDate(em30Dias.getDate() + 30);
  return venc <= em30Dias ? 'vencendo' : 'em_dia';
};

/**
 * Cor e texto de cada situacao.
 *
 * O texto nao e enfeite: a bolinha sozinha e informacao apenas por cor, que
 * nao chega a quem enxerga cor de forma diferente nem a quem usa leitor de
 * tela. Vai como `title` e como texto so-para-leitor.
 */
const APARENCIA_DO_DOCUMENTO: Record<SituacaoDoDocumento, { cor: string; texto: string }> = {
  vencido: { cor: 'bg-red-500', texto: 'vencido' },
  vencendo: { cor: 'bg-amber-500', texto: 'vence em menos de 30 dias' },
  em_dia: { cor: 'bg-green-500', texto: 'em dia' },
  sem_data: { cor: 'bg-gray-300', texto: 'sem data cadastrada' },
};


const VeiculosList: React.FC = () => {
  const { pode } = usePermissoes();
  const [filters, setFilters] = useState({
    status: '',
    categoria: '',
    marca_id: '',
    modelo_id: '',
    placa: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    per_page: 15,
    page: 1
  });

  // O catalogo alimenta os dois filtros. Modelo so e buscado quando ha marca:
  // a rota exige uma, e pedir sem marca seria requisicao garantidamente inutil.
  const [marcas, setMarcas] = useState<VeiculoMarca[]>([]);
  const [modelos, setModelos] = useState<VeiculoModelo[]>([]);

  useEffect(() => {
    catalogoVeiculosService
      .listarMarcas()
      .then((res) => setMarcas(res.data))
      .catch(() => setMarcas([]));
  }, []);

  useEffect(() => {
    if (!filters.marca_id) {
      setModelos([]);
      return;
    }
    let cancelado = false;
    catalogoVeiculosService
      .listarModelos(Number(filters.marca_id))
      .then((res) => { if (!cancelado) setModelos(res.data); })
      .catch(() => { if (!cancelado) setModelos([]); });
    return () => { cancelado = true; };
  }, [filters.marca_id]);

  const navigate = useNavigate();
  const confirm = useConfirm();

  const { data, isLoading, error, refetch } = useQuery<VeiculosResponse>({
    queryKey: ['veiculos', filters],
    queryFn: () => veiculosService.getVeiculos(filters),
    placeholderData: (previousData) => previousData
  });

  const handleDelete = async (veiculo: Veiculo) => {
    const ok = await confirm({
      title: 'Excluir veículo',
      message: `Tem certeza que deseja excluir o veículo ${veiculo.placa}? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await veiculosService.deleteVeiculo(veiculo.id);
      toast.success('Veículo excluído com sucesso');
      refetch();
    } catch (err) {
      toast.error(mensagemDoErro(err, 'Erro ao excluir veículo'));
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    // Voltar para a primeira pagina ao mudar um filtro esta certo: o resultado
    // e outro e a pagina 5 do resultado anterior nao significa nada. Mas isso
    // nao pode valer quando a chave alterada E a pagina, senao o botao Proxima
    // pede a pagina 2 e a linha seguinte devolve para a 1 — a paginacao fica
    // inutilizada sem nenhum sinal de erro.
    setFilters(prev => ({ ...prev, [key]: value, ...(key === 'page' ? {} : { page: 1 }) }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo': return 'bg-green-100 text-green-800';
      case 'inativo': return 'bg-gray-100 text-gray-800';
      case 'manutencao': return 'bg-yellow-100 text-yellow-800';
      case 'vendido': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoriaColor = (categoria: string) => {
    switch (categoria) {
      case 'passeio': return 'bg-blue-100 text-blue-800';
      case 'utilitario': return 'bg-purple-100 text-purple-800';
      case 'comercial': return 'bg-indigo-100 text-indigo-800';
      case 'motocicleta': return 'bg-pink-100 text-pink-800';
      case 'caminhao': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Mover a verificação de erro para useEffect para evitar setState durante render
  useEffect(() => {
    // Falta de permissão não vira aviso passageiro: a tela inteira explica o
    // que aconteceu, logo abaixo, e um toast só somaria ruído.
    if (error && !ehSemPermissao(error)) {
      toast.error(mensagemDoErro(error, 'Erro ao carregar veículos'));
    }
  }, [error]);

  if (ehSemPermissao(error)) {
    return <AvisoSemPermissao erro={error} />;
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Veículos</h1>
          <p className="text-gray-600">Gerencie sua frota de veículos</p>
        </div>
        <div className="flex items-center gap-3">
          {pode('relatorios', 'exportar') && (
            <ExportButtons
            onExportPdf={() => exportService.veiculosPdf()}
            onExportExcel={() => exportService.veiculosExcel()}
          />
          )}
          {pode('veiculos', 'criar') && (
          <Link
            to="/veiculos/novo"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Veículo
          </Link>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
              <option value="manutencao">Manutenção</option>
              <option value="vendido">Vendido</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
            <select
              value={filters.categoria}
              onChange={(e) => handleFilterChange('categoria', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              <option value="passeio">Passeio</option>
              <option value="utilitario">Utilitário</option>
              <option value="comercial">Comercial</option>
              <option value="motocicleta">Motocicleta</option>
              <option value="caminhao">Caminhão</option>
            </select>
          </div>

          {/* Filtro por catalogo, e nao por texto. O `like` antigo casava
              "Ford" com "Fordson" e nao casava "Volkswagen" com "VolksWagen":
              o resultado dependia de como cada pessoa tinha digitado.

              Sem botao de cadastrar aqui, de proposito: filtrar nao e
              cadastrar, e oferecer criacao na busca encheria o catalogo de
              termo de pesquisa. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
            <select
              value={filters.marca_id}
              onChange={(e) => {
                // Trocar a marca limpa o modelo: manter um modelo de outra
                // marca devolveria lista vazia sem explicacao aparente.
                setFilters((prev) => ({ ...prev, marca_id: e.target.value, modelo_id: '', page: 1 }));
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              {marcas.map((m) => (
                <option key={m.id} value={m.id}>{m.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
            <select
              value={filters.modelo_id}
              onChange={(e) => handleFilterChange('modelo_id', e.target.value)}
              disabled={!filters.marca_id}
              title={!filters.marca_id ? 'Escolha a marca primeiro' : undefined}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">{filters.marca_id ? 'Todos' : 'Escolha a marca primeiro'}</option>
              {modelos.map((m) => (
                <option key={m.id} value={m.id}>{m.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Placa</label>
            <input
              type="text"
              value={filters.placa}
              onChange={(e) => handleFilterChange('placa', e.target.value)}
              placeholder="Digite a placa..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Lista de Veículos */}
      <div className="bg-white rounded-lg shadow-sm border">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando veículos...</p>
          </div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Veículo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quilometragem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Documentos
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.data.map((veiculo: Veiculo) => (
                    <tr key={veiculo.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {veiculo.marca} {veiculo.modelo}
                          </div>
                          <div className="text-sm text-gray-500">
                            {veiculo.placa} • {veiculo.ano_fabricacao}/{veiculo.ano_modelo}
                          </div>
                          <div className="text-sm text-gray-500">
                            {veiculo.cor} • {veiculo.combustivel}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(veiculo.status)}`}>
                          {veiculo.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoriaColor(veiculo.categoria)}`}>
                          {veiculo.categoria}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {Number(veiculo.quilometragem).toLocaleString('pt-BR')} km
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {([
                            ['Licenciamento', veiculo.vencimento_licenciamento],
                            ['Seguro', veiculo.vencimento_seguro],
                          ] as const).map(([rotulo, data]) => {
                            const { cor, texto } = APARENCIA_DO_DOCUMENTO[situacaoDoDocumento(data)];
                            return (
                              <div key={rotulo} className="flex items-center gap-2" title={`${rotulo}: ${texto}`}>
                                <span className={`w-2 h-2 rounded-full ${cor}`} aria-hidden="true"></span>
                                {rotulo}
                                <span className="sr-only">: {texto}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <AcoesDaLinha>
                          <BotaoVer onClick={() => navigate(`/veiculos/${veiculo.id}`)} title="Ver detalhes" />
                          {pode('veiculos', 'editar') && <BotaoEditar onClick={() => navigate(`/veiculos/${veiculo.id}/editar`)} />}
                          {pode('veiculos', 'excluir') && <BotaoExcluir onClick={() => handleDelete(veiculo)} />}
                        </AcoesDaLinha>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {data.pagination && data.pagination.last_page > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(data.pagination.current_page - 1)}
                    disabled={data.pagination.current_page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(data.pagination.current_page + 1)}
                    disabled={data.pagination.current_page === data.pagination.last_page}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{((data.pagination.current_page - 1) * data.pagination.per_page) + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(data.pagination.current_page * data.pagination.per_page, data.pagination.total)}
                      </span> de{' '}
                      <span className="font-medium">{data.pagination.total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(data.pagination.current_page - 1)}
                        disabled={data.pagination.current_page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(data.pagination.current_page + 1)}
                        disabled={data.pagination.current_page === data.pagination.last_page}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum veículo encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              Comece criando um novo veículo.
            </p>
            <div className="mt-6">
              <Link
                to="/veiculos/novo"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Novo Veículo
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VeiculosList;
